import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Lock, Loader2, Eye, EyeOff, ShieldCheck, KeyRound,
  AlertCircle, CheckCircle2, RefreshCcw, History, LogOut, Save, Zap,
} from 'lucide-react';

import { getApiBase } from '../lib/apiBase';
import AuditLogPanel from '../components/AuditLogPanel';
import PaymentRecordsPanel from '../components/PaymentRecordsPanel';

/**
 * /admin/stripe-settings
 *
 * Secure admin UI for managing Stripe credentials. Per the spec:
 *   - Backend-validated bcrypt+JWT login (15-min token, held in memory only)
 *   - Inputs are type=password, never echoed back
 *   - Server returns only masked + last 4 — never the full key
 *   - Test connection uses invalid-session probe (no Balance:Read needed)
 *   - Audit log shows admin email, timestamp, last 4 only
 */

const API = getApiBase();

const ENV_OPTIONS = [
  { value: 'live', label: 'Live' },
  { value: 'test', label: 'Test' },
];

const StripeSettingsPage = () => {
  const [token, setToken] = useState(null);
  const [adminUser, setAdminUser] = useState(null);

  // Login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState('');

  // Config form
  const [environment, setEnvironment] = useState('live');
  const [secretKey, setSecretKey] = useState('');
  const [publishableKey, setPublishableKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveOk, setSaveOk] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Server state
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Change password
  const [showPwForm, setShowPwForm] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwResult, setPwResult] = useState('');

  const authedFetch = useCallback(
    async (path, options = {}) => {
      const res = await fetch(`${API}${path}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...(options.headers || {}),
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.status === 401) {
        setToken(null);
        setAdminUser(null);
        throw new Error('Session expired. Please log in again.');
      }
      let data;
      const text = await res.text();
      try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
      if (!res.ok) {
        throw new Error(data.detail || `Request failed (${res.status})`);
      }
      return data;
    },
    [token]
  );

  const loadEverything = useCallback(async () => {
    try {
      const cfg = await authedFetch('/api/admin/stripe-config');
      setConfig(cfg);
      setConfigError('');
    } catch (err) {
      setConfigError(err.message);
    }
  }, [authedFetch]);

  useEffect(() => {
    if (token) {
      loadEverything();
    }
  }, [token, loadEverything]);

  // Defensive: on mount, purge any stale admin_token / admin_session left
  // by an older build or a browser extension. Ensures the login page ALWAYS
  // renders in a fresh unauthenticated state.
  useEffect(() => {
    try {
      ['admin_token', 'admin_session', 'sag_admin_token', 'stripe_admin_token']
        .forEach((k) => { localStorage.removeItem(k); sessionStorage.removeItem(k); });
    } catch { /* storage APIs blocked — ignore */ }
  }, []);

  // Watchdog: warn once (dev + prod) if a script outside our bundle is
  // firing /api/admin/* requests from this page. Common cause is a Chrome
  // extension injecting Stripe.js — not something we can silence, but
  // we surface the diagnostic so it's not confused with our code.
  useEffect(() => {
    if (typeof window === 'undefined' || window.__sag_admin_watchdog__) return;
    window.__sag_admin_watchdog__ = true;
    const origFetch = window.fetch;
    let externalCalls = 0;
    window.fetch = function patchedFetch(input, init) {
      try {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        if (url.includes('/api/admin/') && !url.includes(`${API}/api/`) && !token) {
          externalCalls += 1;
          if (externalCalls === 1) {
            // eslint-disable-next-line no-console
            console.warn(
              '[SAG watchdog] An external caller (likely a browser extension) is ' +
              'hitting /api/admin/* on the login page. This is NOT our code. ' +
              'Full URL:', url
            );
          }
        }
      } catch { /* never break fetch over diagnostics */ }
      return origFetch.apply(this, arguments);
    };
    // NOTE: no cleanup — the patch is a one-time diagnostic that lives
    // for the page lifetime. Cleaning up on unmount + the module-global
    // guard flag would leave fetch unpatched forever after StrictMode's
    // double-mount and defeat the watchdog.
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError('');
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Login failed');
      setToken(data.access_token);
      setAdminUser(data.user);
      setPassword('');
      if (data.user?.must_change_password) {
        setShowPwForm(true);
      }
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    setToken(null);
    setAdminUser(null);
    setConfig(null);
    setSecretKey('');
    setPublishableKey('');
    setWebhookSecret('');
    setTestResult(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveOk(false);
    setSaveError('');
    setTestResult(null);
    try {
      const payload = { environment };
      if (secretKey.trim()) payload.secret_key = secretKey.trim();
      if (publishableKey.trim()) payload.publishable_key = publishableKey.trim();
      if (webhookSecret.trim()) payload.webhook_secret = webhookSecret.trim();
      if (!payload.secret_key && !payload.publishable_key && !payload.webhook_secret) {
        throw new Error('Enter at least one key to save.');
      }
      await authedFetch('/api/admin/stripe-config', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      setSaveOk(true);
      setSecretKey('');
      setPublishableKey('');
      setWebhookSecret('');
      await loadEverything();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const data = await authedFetch('/api/admin/stripe-config/test', { method: 'POST' });
      setTestResult(data);
      await loadEverything();
    } catch (err) {
      setTestResult({ ok: false, message: err.message });
    } finally {
      setTesting(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwSaving(true);
    setPwResult('');
    try {
      await authedFetch('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ current_password: currentPw, new_password: newPw }),
      });
      setPwResult('Password updated.');
      setCurrentPw('');
      setNewPw('');
      setAdminUser({ ...adminUser, must_change_password: false });
    } catch (err) {
      setPwResult(err.message);
    } finally {
      setPwSaving(false);
    }
  };

  // ── Render: login ─────────────────────────────────────────────────────
  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4" data-testid="stripe-admin-login">
        <div className="w-full max-w-md bg-white border border-gray-200 p-8">
          <div className="text-center mb-8">
            <div className="w-12 h-12 bg-[#C9A227]/10 flex items-center justify-center mx-auto mb-3">
              <ShieldCheck className="w-6 h-6 text-[#C9A227]" />
            </div>
            <h1 className="text-xl font-medium text-black tracking-wide">Stripe Configuration</h1>
            <p className="text-gray-500 text-sm mt-1">Admin access required</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                className="w-full px-4 py-3 border border-gray-200 text-sm focus:outline-none focus:border-[#C9A227]"
                data-testid="admin-login-email"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="w-full px-4 py-3 pr-10 border border-gray-200 text-sm focus:outline-none focus:border-[#C9A227]"
                  data-testid="admin-login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {loginError && (
              <p className="text-xs text-red-600" data-testid="admin-login-error">{loginError}</p>
            )}
            <button
              type="submit"
              disabled={loggingIn}
              className="w-full py-3 bg-black text-white text-xs tracking-[0.15em] uppercase font-medium hover:bg-[#1e3a5f] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="admin-login-submit"
            >
              {loggingIn ? 'Signing in…' : 'Sign In'}
            </button>
          </form>

          <p className="text-center text-[10px] text-gray-400 mt-6 tracking-wider uppercase">
            Sebastian Alliance Group · Secure Administration
          </p>
        </div>
      </div>
    );
  }

  // ── Render: authenticated dashboard ──────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50" data-testid="stripe-admin-dashboard">
      <header className="bg-black text-white py-4 px-4 md:px-8">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 text-white/70 hover:text-white text-sm" data-testid="admin-back-home">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline tracking-wide">Back to Site</span>
          </a>
          <span className="text-white/40 text-[10px] tracking-[0.3em] uppercase">Stripe Configuration</span>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-white/70 hover:text-white text-sm"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Sign Out</span>
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto py-10 px-4 md:px-8 space-y-8">
        <div className="bg-white border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 bg-[#1e3a5f]/10 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-[#1e3a5f]" />
            </div>
            <div>
              <h2 className="text-base font-medium text-black">{adminUser?.email}</h2>
              <p className="text-xs text-gray-500">Authenticated admin — session expires after 15 minutes of inactivity</p>
            </div>
          </div>

          {adminUser?.must_change_password && !showPwForm && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 text-sm text-amber-900 flex items-start gap-2" data-testid="must-change-pw">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                You are using the seed password. Please change it before continuing.
              </div>
              <button onClick={() => setShowPwForm(true)} className="text-amber-900 underline text-xs">Change password</button>
            </div>
          )}

          {showPwForm && (
            <form onSubmit={handleChangePassword} className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end" data-testid="change-pw-form">
              <div>
                <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1">Current Password</label>
                <input type="password" required value={currentPw} onChange={(e) => setCurrentPw(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-[#C9A227]" data-testid="current-pw" />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1">New Password (12+ chars)</label>
                <input type="password" required minLength={12} value={newPw} onChange={(e) => setNewPw(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-[#C9A227]" data-testid="new-pw" />
              </div>
              <button type="submit" disabled={pwSaving} className="px-4 py-2 bg-[#C9A227] text-black text-xs tracking-wider uppercase font-medium hover:bg-[#b8911f] disabled:opacity-60" data-testid="submit-pw-change">
                {pwSaving ? 'Saving…' : 'Update Password'}
              </button>
              {pwResult && <p className="sm:col-span-3 text-xs text-gray-700" data-testid="pw-result">{pwResult}</p>}
            </form>
          )}
        </div>

        {/* Active key source banner — single source of truth for which key
            checkout actually uses right now. Shows BEFORE the saved-keys card
            so the admin cannot miss it. */}
        {config && (
          <section
            className={`border p-5 ${
              config.active_source === 'vault'
                ? 'bg-emerald-50 border-emerald-200'
                : config.active_source === 'env_forced'
                ? 'bg-amber-50 border-amber-200'
                : config.active_source === 'env_fallback'
                ? 'bg-amber-50 border-amber-200'
                : 'bg-red-50 border-red-200'
            }`}
            data-testid="active-source-banner"
          >
            <div className="flex items-start gap-3">
              <Zap className={`w-5 h-5 mt-0.5 ${
                config.active_source === 'vault' ? 'text-emerald-600' :
                config.active_source === 'none' ? 'text-red-600' : 'text-amber-600'
              }`} />
              <div className="flex-1 text-sm">
                <p className="font-medium text-black">
                  Checkout is using:{' '}
                  <span data-testid="active-source-label">
                    {config.active_source === 'vault' && 'the key saved in this Admin UI (vault)'}
                    {config.active_source === 'env_forced' && 'the STRIPE_SECRET_KEY env var (STRIPE_KEY_PREFER_ENV=true is set)'}
                    {config.active_source === 'env_fallback' && 'the STRIPE_SECRET_KEY env var (vault is empty)'}
                    {config.active_source === 'none' && '— no Stripe key configured —'}
                  </span>
                </p>
                {config.active_last4 && (
                  <p className="mt-1 text-xs text-gray-700" data-testid="active-source-last4">
                    Active key ends in <span className="font-mono text-black">{config.active_prefix}…{config.active_last4}</span>
                  </p>
                )}
                {config.env_var_present_but_ignored && (
                  <p className="mt-2 text-xs text-emerald-800">
                    A STRIPE_SECRET_KEY env var is also set but it is being ignored (vault wins). You can safely clear the env var in Emergent → Settings → Environment Variables.
                  </p>
                )}
                {config.active_source === 'env_forced' && (
                  <p className="mt-2 text-xs text-amber-900">
                    ⚠️ The env var is overriding the vault because STRIPE_KEY_PREFER_ENV=true. Unset that flag in Emergent to make the UI-saved key authoritative again.
                  </p>
                )}
                {config.active_source === 'env_fallback' && (
                  <p className="mt-2 text-xs text-amber-900">
                    The vault is empty, so checkout is falling back to the env var. Paste a key below to make the UI the source of truth.
                  </p>
                )}
                {config.active_source === 'none' && (
                  <p className="mt-2 text-xs text-red-800">
                    Checkout will fail with "Stripe not configured" until a key is saved below or STRIPE_SECRET_KEY is set in Emergent.
                  </p>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Current saved keys */}
        <section className="bg-white border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-black flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-[#1e3a5f]" />
              Saved Stripe Credentials ({config?.environment || '—'})
            </h3>
            <button onClick={loadEverything} className="flex items-center gap-1 text-xs text-gray-500 hover:text-black" data-testid="refresh-config">
              <RefreshCcw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>
          {configError && (
            <p className="text-xs text-red-600 mb-3">{configError}</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <SavedRow label="Secret Key" testid="saved-secret-key"
              masked={config?.secret_key_masked}
              meta={config?.secret_key_updated_at && `${formatDate(config.secret_key_updated_at)} · ${config.secret_key_updated_by || ''}`} />
            <SavedRow label="Publishable Key" testid="saved-publishable-key"
              masked={config?.publishable_key_masked}
              meta={config?.publishable_key_updated_at && formatDate(config.publishable_key_updated_at)} />
            <SavedRow label="Webhook Secret" testid="saved-webhook-secret"
              masked={config?.webhook_secret_masked}
              meta={config?.webhook_secret_updated_at && formatDate(config.webhook_secret_updated_at)} />
          </div>
          {config?.expected_stripe_account_id && (
            <p className="mt-3 text-xs text-gray-500" data-testid="expected-account-id">
              Expected Stripe account: <span className="font-mono text-black">{config.expected_stripe_account_id}</span>
            </p>
          )}
          {config?.using_env_fallback && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 text-xs text-amber-900" data-testid="env-fallback-warning">
              ⚠️ The encrypted vault is empty. Payments are falling back to the STRIPE_SECRET_KEY in <code>backend/.env</code>. Save a key here to switch the source of truth back to the database.
            </div>
          )}
          <div className="mt-5 flex flex-wrap gap-3">
            <button
              onClick={handleTest}
              disabled={testing || !config?.secret_key_masked}
              className="px-5 py-2 border border-[#1e3a5f] text-[#1e3a5f] text-xs tracking-wider uppercase font-medium hover:bg-[#1e3a5f] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              data-testid="test-connection"
            >
              {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
              {testing ? 'Testing…' : 'Test Stripe Connection'}
            </button>
            {testResult && (
              <span
                className={`inline-flex items-center gap-1.5 text-xs px-3 py-2 ${testResult.ok ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}
                data-testid="test-result"
              >
                {testResult.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                {testResult.message}
              </span>
            )}
          </div>
        </section>

        {/* Update form */}
        <section className="bg-white border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-black mb-4 flex items-center gap-2">
            <Save className="w-4 h-4 text-[#1e3a5f]" />
            Update Stripe Keys
          </h3>
          <form onSubmit={handleSave} className="space-y-4" data-testid="update-form">
            <div>
              <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1">Environment</label>
              <select
                value={environment}
                onChange={(e) => setEnvironment(e.target.value)}
                className="w-full sm:w-48 px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-[#C9A227]"
                data-testid="env-select"
              >
                {ENV_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <PasswordField label="Restricted Secret Key (rk_live_… or sk_live_…)"
              value={secretKey} onChange={setSecretKey} testid="input-secret-key" />
            <PasswordField label="Publishable Key (pk_live_…) — optional"
              value={publishableKey} onChange={setPublishableKey} testid="input-publishable-key" />
            <PasswordField label="Webhook Secret (whsec_…) — optional"
              value={webhookSecret} onChange={setWebhookSecret} testid="input-webhook-secret" />
            <p className="text-[11px] text-gray-500">
              Keys are encrypted at rest with Fernet (AES-128-CBC + HMAC). They are never displayed back, never logged, and never returned by any other API.
            </p>
            <div className="flex items-center gap-3">
              <button type="submit" disabled={saving} className="px-5 py-3 bg-black text-white text-xs tracking-[0.15em] uppercase font-medium hover:bg-[#1e3a5f] disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2" data-testid="save-keys">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                {saving ? 'Saving…' : 'Save Securely'}
              </button>
              {saveOk && (
                <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700" data-testid="save-ok">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Saved
                </span>
              )}
              {saveError && (
                <span className="inline-flex items-center gap-1.5 text-xs text-red-700" data-testid="save-error">
                  <AlertCircle className="w-3.5 h-3.5" /> {saveError}
                </span>
              )}
            </div>
          </form>
        </section>

        {/* Payment Configuration Audit — searchable + filterable + exportable */}
        <PaymentRecordsPanel token={token} />
        <AuditLogPanel authedFetch={authedFetch} API={API} token={token} />
      </div>
    </div>
  );
};

const SavedRow = ({ label, masked, meta, testid }) => (
  <div className="p-3 bg-gray-50 border border-gray-100">
    <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1">{label}</div>
    <div className="font-mono text-sm text-black break-all" data-testid={testid}>
      {masked || <span className="text-gray-400">Not set</span>}
    </div>
    {meta && <div className="text-[10px] text-gray-400 mt-1">{meta}</div>}
  </div>
);

const PasswordField = ({ label, value, onChange, testid }) => {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1">{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="off"
          spellCheck="false"
          className="w-full px-3 py-2 pr-10 border border-gray-200 text-sm font-mono focus:outline-none focus:border-[#C9A227]"
          data-testid={testid}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          aria-label={show ? 'Hide' : 'Show'}
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const prettyAction = (a) => ({
  'admin.login': 'Login',
  'admin.stripe_key_updated': 'Stripe key updated',
  'admin.stripe_test_connection': 'Test connection',
  'admin.password_changed': 'Password changed',
}[a] || a);

export default StripeSettingsPage;
